/*==============================================================================
  
  #IMPORTANT INFO WHEN WORKING ON THIS FILE

  This is the file for development. Include all development work here and then 
  minify into theme.min.js

  ------------------------------------------------------------------------------

  #DEVELOPMENT INFO

  ###EVENT DELEGATION
  - We utilise the Dom Delegate library in `vendor.js` to better manage event
    delegation.
  - Search `new Delegate` within this file for examples
  - For more info:
    https://github.com/Financial-Times/ftdomdelegate

  ###FUNCTION NAMING
  - Module names are capitalised e.g. `theme.HeaderSection`
  - Functions/methods within modules are camelcase and include an underscore 
    if they are a private method. E.g:
    `_privateFunction` vs. `publicFunction`


  ------------------------------------------------------------------------------

  #TABLE OF CONTENTS

  - Utilities               Utility helpers
  - Accessibility           Accessibility helpers
  - Sections                Shopify Theme Editor API
  - Currency                Currency helper and formatting
  - LoadImageSection        Lazyloading JS hook
  - Responsive              Responsive helpers

  - MobileNavigation        
  - Desktop Navigation      
  - Header Section

  - Product Variants
  - Product Gallery
  - Product Video
  - Product Model
  - Product Zoom Modal
  - Quantity Picker

  - Cart Section
  - Tabs
  - Scroll Snap Carousel
  - Product Section
  - Product Recommendations
  - Slideshow Section
  - Hero Video Section

  - Login Section
  - Account Section
  - Country Selector
  - Gift Card Section
  - Modal Manager
  - Value Picker

  - Tabbing a11y
  - Page Transitions
  - Product Item
  - DOM Ready
==============================================================================*/


window.theme = window.theme || {};

theme.Config = {};

(function(){
  'use strict';

  /*==============================================================================
    #Utilities
    - Useful utilities used across our JavaScript
    - defaultTo, debounce, throttle, compact, serialize, getSiblings
  ==============================================================================*/

  theme.Utilities = {
    /**
     * _.defaultTo from lodash
     * Checks `value` to determine whether a default value should be returned in
     * its place. The `defaultValue` is returned if `value` is `NaN`, `null`,
     * or `undefined`.
     * Source: https://github.com/lodash/lodash/blob/master/defaultTo.js
     *
     * @param {*} value - Value to check
     * @param {*} defaultValue - Default value
     * @returns {*} - Returns the resolved value
     */
    defaultTo: function(value, defaultValue) {
      return (value == null || value !== value) ? defaultValue : value
    },
  
    debounce: function(wait, callback, immediate) {
      var timeout;
      return function() {
        var context = this, args = arguments;
        var later = function() {
          timeout = null;
          if (!immediate) callback.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) callback.apply(context, args);
      }
    },
  
    throttle: function(limit, callback) {
      var waiting = false;
      return function () {
        if (!waiting) {
          callback.apply(this, arguments);
          waiting = true;
          setTimeout(function () {
            waiting = false;
          }, limit);
        }
      }
    },
    
    /**
     * _.compact from lodash
     * Creates an array with all falsey values removed. The values `false`, `null`,
     * `0`, `""`, `undefined`, and `NaN` are falsey.
     * _.compact([0, 1, false, 2, '', 3]);
     * => [1, 2, 3] 
     */

    compact: function(array) {
      var index = -1,
          length = array == null ? 0 : array.length,
          resIndex = 0,
          result = [];
  
      while (++index < length) {
        var value = array[index];
        if (value) {
          result[resIndex++] = value;
        }
      }
      return result;
    },
  
    serialize: function(form) {
      var arr = [];
      Array.prototype.slice.call(form.elements).forEach(function(field) {
        if (
          !field.name ||
          field.disabled ||
          ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
        )
          return;
        if (field.type === 'select-multiple') {
          Array.prototype.slice.call(field.options).forEach(function(option) {
            if (!option.selected) return;
            arr.push(
              encodeURIComponent(field.name) +
                '=' +
                encodeURIComponent(option.value)
            );
          });
          return;
        }
        if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked)
          return;
        arr.push(
          encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value)
        );
      });
      return arr.join('&');
    },

    /**
     * Get all the previous and next siblings, optionally filtered by a selector
     */
    getSiblings: function(element, filter) {
      var includeSelf = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

      var siblings = [];
      var currentElement = element;

      // Do the previous first
      while (currentElement = currentElement.previousElementSibling) {
        if (!filter || currentElement.matches(filter)) {
          siblings.push(currentElement);
        }
      }

      if (includeSelf) {
        siblings.push(element);
      }

      // Then the next side
      currentElement = element;

      while (currentElement = currentElement.nextElementSibling) {
        if (!filter || currentElement.matches(filter)) {
          siblings.push(currentElement);
        }
      }

      return siblings;
    }
  };










  /*==============================================================================
    #Accessibility (a11y) Helpers
    - Standard accessibility helpers to deal with focus
    - trapFocus, removeTrapFocus, clearTrapFocus
  ==============================================================================*/

  theme.Accessibility = {

    trapFocus: function(container, namespace) {
      this.listeners = this.listeners || {};

      // Check for an element with "autofocus" attribute
      var elementToFocus = container.querySelector('[autofocus]') || container;
      container.setAttribute('tabindex', '-1');
      
      elementToFocus.focus();

      this.listeners[namespace] = function(event) {
        if(container !== event.target && !container.contains(event.target)) {
          elementToFocus.focus();
        }
      };

      document.addEventListener('focusin', this.listeners[namespace]);
    },


    removeTrapFocus: function(container, namespace) {
      if (container) {
        container.removeAttribute('tabindex');
      }

      document.removeEventListener('focusin', this.listeners[namespace]);
    },


    clearTrapFocus: function() {
      for (var key in this.listeners) {
        if (this.listeners.hasOwnProperty(key)) {
          document.removeEventListener('focusin', this.listeners[key]);
        }
      }

      this.listeners = {};
    }
  };










  theme.Sections = function Sections() {
    this.constructors = {};
    this.instances = [];
  
    document.addEventListener('shopify:section:load', this._onSectionLoad.bind(this));
    document.addEventListener('shopify:section:unload', this._onSectionUnload.bind(this));
    document.addEventListener('shopify:section:select', this._onSelect.bind(this));
    document.addEventListener('shopify:section:deselect', this._onDeselect.bind(this));
    document.addEventListener('shopify:block:select', this._onBlockSelect.bind(this));
    document.addEventListener('shopify:block:deselect', this._onBlockDeselect.bind(this));
  };
  
  theme.Sections.prototype = Object.assign({}, theme.Sections.prototype, {
    _createInstance: function(container, constructor, scope) {
      var id = container.getAttribute('data-section-id');
      var type = container.getAttribute('data-section-type');
  
      constructor = constructor || this.constructors[type];
  
      if (typeof constructor === 'undefined') {
        return;
      }
  
      // If custom scope passed, check to see if instance
      // is already initialized so we don't double up
      if (scope) {
        var instanceExists = this._findInstance(id);
        if (instanceExists) { return }
      }
  
      var instance = Object.assign(new constructor(container), {
        id: id,
        type: type,
        container: container
      });
  
      this.instances.push(instance);
    },
  
    _findInstance: function(id) {
      for (var i = 0; i < this.instances.length; i++) {
        if (this.instances[i].id === id) {
          return this.instances[i];
        }
      }
    },
  
    _onSectionLoad: function(evt, subSection, subSectionId) {

      // Run sal - animation library in `vendor.js``
      sal();
      
      var container = subSection ? subSection : evt.target;
      var section = subSection ? subSection : evt.target.querySelector('[data-section-id]');
  
      if (!section) {
        return;
      }
  
      this._createInstance(section);
  
      var instance = subSection ? subSectionId : this._findInstance(evt.detail.sectionId);
  
      // Check if we have subsections to load
      var haveSubSections = container.querySelectorAll('[data-subsection]');
      if (haveSubSections.length) {
        this._loadSubSections();
      }
  
      // Run JS only in case of the section being selected in the editor
      // before merchant clicks "Add"
      if (instance && typeof instance.onLoad === 'function') {
        instance.onLoad(evt);
      }
  
      // Force editor to trigger scroll event when loading a section
      setTimeout(function() {
        window.dispatchEvent(new Event('scroll'));
      }, 200);
    },
  
    _onSectionUnload: function(evt) {
      this.instances = this.instances.filter(function(instance) {
        var isEventInstance = instance.id === evt.detail.sectionId;
  
        if (isEventInstance) {
          if (typeof instance.onUnload === 'function') {
            instance.onUnload(evt);
          }
        }
  
        return !isEventInstance;
      });
    },
  
    _loadSubSections: function() {  
      document.querySelectorAll('[data-subsection]').forEach(el => {
        this._onSectionLoad(null, el, el.dataset.sectionId);
      });
    },
  
    _onSelect: function(evt) {
      var instance = this._findInstance(evt.detail.sectionId);
  
      if (
        typeof instance !== 'undefined' &&
        typeof instance.onSelect === 'function'
      ) {
        instance.onSelect(evt);
      }
    },
  
    _onDeselect: function(evt) {
      var instance = this._findInstance(evt.detail.sectionId);
  
      if (
        typeof instance !== 'undefined' &&
        typeof instance.onDeselect === 'function'
      ) {
        instance.onDeselect(evt);
      }
    },
  
    _onBlockSelect: function(evt) {
      var instance = this._findInstance(evt.detail.sectionId);
  
      if (
        typeof instance !== 'undefined' &&
        typeof instance.onBlockSelect === 'function'
      ) {
        instance.onBlockSelect(evt);
      }
    },
  
    _onBlockDeselect: function(evt) {
      var instance = this._findInstance(evt.detail.sectionId);
  
      if (
        typeof instance !== 'undefined' &&
        typeof instance.onBlockDeselect === 'function'
      ) {
        instance.onBlockDeselect(evt);
      }
    },
  
    register: function(type, constructor, scope) {
      this.constructors[type] = constructor;
  
      var sections = document.querySelectorAll('[data-section-type="' + type + '"]');
  
      if (scope) {
        sections = scope.querySelectorAll('[data-section-type="' + type + '"]');
      }
  
      sections.forEach(
        function(container) {
          this._createInstance(container, constructor, scope);
        }.bind(this)
      );
    }
  });











  /*==============================================================================
    #Currency
    - Standard currency formatting for Shopify
  ==============================================================================*/

  theme.Currency = (function() {
    var moneyFormat = '${{amount}}';
  
    function formatMoney(cents, format) {
      if (!format) {
        format = theme.settings.moneyFormat;
      }
  
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      var value = '';
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = (format || moneyFormat);
  
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = theme.Utilities.defaultTo(precision, 2);
        thousands = theme.Utilities.defaultTo(thousands, ',');
        decimal = theme.Utilities.defaultTo(decimal, '.');
  
        if (isNaN(number) || number == null) {
          return 0;
        }
  
        number = (number / 100.0).toFixed(precision);
  
        var parts = number.split('.');
        var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        var centsAmount = parts[1] ? (decimal + parts[1]) : '';
  
        return dollarsAmount + centsAmount;
      }
  
      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;

        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;

        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;

        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;

        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;

        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
      }
  
      if (formatString.indexOf('with_comma_separator') !== -1) {
        return formatString.replace(placeholderRegex, value);
      } else {
        return formatString.replace(placeholderRegex, value);
      }
    }
  
    return {
      formatMoney: formatMoney
    }
  })();













  /*==============================================================================
    #Load Image Section
    - Allows us to run loading animations whilst lazy loading.
    - You can add manual CSS by adding `loading` or `loading--delayed` classes
      into markup
    - @param {container} The element you want to watch for lazyloading
  ==============================================================================*/

  theme.LoadImageSection = function(container) {

    // Wait until images inside container have lazyloaded class
    function setAsLoaded() {
      container.classList.remove('loading', 'loading--delayed');
      container.classList.add('loaded');
    }
  
    function checkForLazyloadedImage() {
      return container.querySelector('.lazyloaded');
    }
  
    // If it has SVGs it's in the onboarding state so set as loaded
    if (container.querySelector('svg')) {
      setAsLoaded();
      return;
    };
  
    if (checkForLazyloadedImage()) {
      setAsLoaded();
      return;
    }
  
    var interval = setInterval(function() {
      if (checkForLazyloadedImage()) {
        clearInterval(interval);
        setAsLoaded();
      }
    }, 80);
  };










  /*==============================================================================
    #Responsive
    - Responsive helpers allowing to get current breakpoint and check if we
      match a specific breakpoint. These breakpoints are taken from our CSS.
  ==============================================================================*/

  theme.Responsive = (function() {
    var currentBreakpoint = getCurrentBreakpoint();

    window.addEventListener('resize', function() {
      var newBreakpoint = getCurrentBreakpoint();

      if (currentBreakpoint === newBreakpoint) {
        return;
      }

      document.dispatchEvent(new CustomEvent('breakpoint:changed', { detail: {
        previousBreakpoint: currentBreakpoint,
        currentBreakpoint: newBreakpoint
      } }));

      currentBreakpoint = newBreakpoint;
    });

    function matchesBreakpoint(breakpoint) {
      switch (breakpoint) {
        case 'small':
          return window.matchMedia('screen and (max-width: 767px)').matches;
        case 'medium':
          return window.matchMedia('screen and (min-width: 768px) and (max-width: 1024px)').matches;
        case 'medium-up':
          return window.matchMedia('screen and (min-width: 768px)').matches;
        case 'medium-down':
          return window.matchMedia('screen and (max-width: 1024px)').matches;
        case 'large':
          return window.matchMedia('screen and (min-width: 1025px)').matches;
        case 'supports-hover':
          return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      }
    }

    function getCurrentBreakpoint() {
      if (window.matchMedia('screen and (max-width: 767px)').matches) {
        return 'small';
      }

      if (window.matchMedia('screen and (min-width: 768px) and (max-width: 1024px)').matches) {
        return 'medium';
      }

      if (window.matchMedia('screen and (min-width: 1025px)').matches) {
        return 'large';
      }
    }

    return {
      getCurrentBreakpoint: getCurrentBreakpoint,
      matchesBreakpoint: matchesBreakpoint
    }
  })();










  /*==============================================================================
    #MOBILE NAVIGATION
  ==============================================================================*/

  theme.MobileNavigation = (function() {
    function MobileNavigation(element) {
      this.element = element;
      this.delegateElement = new Delegate(this.element);
      this.delegateRoot = new Delegate(document.documentElement);

      this.mobileMenuElement = this.element.querySelector('.mobile-menu');
      this.mobileMenuToggleElement = this.element.querySelector("[aria-controls=\"".concat(this.mobileMenuElement.id, "\"]"));
      this.isOpen = false;

      this._attachListeners();
    }

    MobileNavigation.prototype = Object.assign({}, MobileNavigation.prototype, {

      destroy: function() {
        this.delegateElement.off();
        this.delegateRoot.off();
        window.removeEventListener('resize', this._calculatMaxHeightListener);
      },

      _attachListeners: function() {
        this._calculatMaxHeightListener = this._calculateMaxHeight.bind(this);
        this.delegateElement.on('click', '[data-action="toggle-menu"]', this._toggleMenu.bind(this));
        this.delegateRoot.on('click', this._onWindowClick.bind(this));
        window.addEventListener('resize', this._calculatMaxHeightListener);
      },

      _toggleMenu: function() {
        this.isOpen = !this.isOpen;
        this.mobileMenuToggleElement.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
        this.mobileMenuElement.setAttribute('aria-hidden', this.isOpen ? 'false' : 'true');

        if (!this.isOpen) {
          this.mobileMenuElement.style.maxHeight = '';
          document.body.classList.remove('no-mobile-scroll');
        } else {
          // We need to restrict the height
          this._calculateMaxHeight();

          document.body.classList.add('no-mobile-scroll');
        }
      },

      _calculateMaxHeight: function() {
        if (this.isOpen) {
          this.mobileMenuElement.style.maxHeight = "".concat(window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom, "px");
        }
      },

      _onWindowClick: function(event) {
        if (this.isOpen && !this.element.contains(event.target)) {
          this._toggleMenu();
        }
      }
    });

    return MobileNavigation;
  })();










  /*==============================================================================
    #DESKTOP NAVIGATION
  ==============================================================================*/

  theme.DesktopNavigation = (function() {
    function DesktopNavigation(element) {

      this.element = element;
      this.delegateElement = new Delegate(this.element);
      this.delegateRoot = new Delegate(document.documentElement);

      this.desktopMenuElement = this.element.querySelector('.desktop-menu');
      this.desktopMenuToggleElement = this.element.querySelector("[aria-controls=\"".concat(this.desktopMenuElement.id, "\"]"));
      this.isOpen = false;

      this._attachListeners();
    }

    DesktopNavigation.prototype = Object.assign({}, DesktopNavigation.prototype, {

      destroy: function() {
        this.delegateElement.off();
        this.delegateRoot.off();
        window.removeEventListener('resize', this._calculatMaxHeightListener);
      },

      _attachListeners: function() {
        this._calculatMaxHeightListener = this._calculateMaxHeight.bind(this);
        this.delegateElement.on('click', '[data-action="toggle-menu"]', this._toggleMenu.bind(this));
        this.delegateRoot.on('click', this._onWindowClick.bind(this));
        window.addEventListener('resize', this._calculatMaxHeightListener);
      },

      _toggleMenu: function() {
        this.isOpen = !this.isOpen;
        this.desktopMenuToggleElement.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
        this.desktopMenuElement.setAttribute('aria-hidden', this.isOpen ? 'false' : 'true');

        if (!this.isOpen) {
          this.desktopMenuElement.style.maxHeight = '';
          document.body.classList.remove('no-mobile-scroll');
        } else {
          // We need to restrict the height
          this._calculateMaxHeight();

          document.body.classList.add('no-mobile-scroll');
        }
      },

      _calculateMaxHeight: function() {
        if (this.isOpen) {
          this.desktopMenuElement.style.maxHeight = "".concat(window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom, "px");
        }
      },

      _onWindowClick: function(event) {
        if (this.isOpen && !this.element.contains(event.target)) {
          this._toggleMenu();
        }
      }
    });

    return DesktopNavigation;
  })();










  /*==============================================================================
    #HEADER SECTION
  ==============================================================================*/

  theme.HeaderSection = (function() {

    function HeaderSection(container) {
      this.container = container;
      this.delegateElement = new Delegate(this.container);
      this.delegateRoot = new Delegate(document.documentElement);

      var sectionId = this.sectionId = container.getAttribute('data-section-id');
      var options = this.options = JSON.parse(container.getAttribute('data-section-settings'));

      var header = this.header = document.querySelector('.shopify-section--header');

      var config = this.config = {
        prevScrollPos: 0
      };

      var mobileNavigationElement = this.container.querySelector('.header__mobile-nav');

      if (mobileNavigationElement) {
        this.mobileNavigation = new theme.MobileNavigation(mobileNavigationElement);
      }

      var desktopNavigationElement = this.container.querySelector('.header__desktop-nav');

      if (desktopNavigationElement) {
        this.desktopNavigation = new theme.DesktopNavigation(desktopNavigationElement);
      }

      this._attachListeners();
    }

    HeaderSection.prototype = Object.assign({}, HeaderSection.prototype, {
      _attachListeners: function() {
        window.addEventListener('scroll', this._scrollListener.bind(this));
      },

      /**
       * Hide header when scrolled past 1 viewport of the page. Show header
       * when scrolling back up.
       */
      _scrollListener: function() {
        var hasScrolledUp = false;
        var pageHeight = window.innerHeight;
        var self = this;
        var scrollPos;

        fastdom.measure(function() {
          scrollPos = window.scrollY;
        });

        fastdom.mutate(function() {
          if (scrollPos > pageHeight && self.config.prevScrollPos < scrollPos) {
            self.header.classList.add('shopify-section--header--is-hidden');
            hasScrolledUp = false;
          } else {
            self.header.classList.remove('shopify-section--header--is-hidden');
            hasScrolledUp = true;
          }

          fastdom.measure(function() {
            self.config.prevScrollPos = scrollPos;
          });
        });
      }
    });

    return HeaderSection;
  })();










  /*==============================================================================
    #Product Variants
    - A complex module that handles all product variant functionality
    - The module features dynamic variant availability meaning we can provide
      a visual indication of unavailable variants.
    - A thorough explanation of dynamic variants is shown here:
      https://archetypethemes.co/blogs/support/dynamic-variant-availability
  ==============================================================================*/

  theme.ProductVariants = (function() {

    function ProductVariants(element, options) {

      var _this = this;

      this.element = element;
      this.delegateElement = new Delegate(this.element);
      this.options = options;

      var productJsonElement = this.element.querySelector('[data-product-json]');

      if (productJsonElement) {

        var jsonData = JSON.parse(productJsonElement.innerHTML);

        this.productData = jsonData['product'];
        this.productOptionsWithValues = jsonData['options_with_values'];
        this.variantSelectors = this.element.querySelectorAll('.product-form__option[data-selector-type]');
        this.masterSelector = this.element.querySelector("#product-select-".concat(this.productData['id'])); 

        // We init value with the first selected variant
        this.productData['variants'].forEach(function (variant) {
          if (variant['id'] === jsonData['selected_variant_id']) {
            _this.currentVariant = variant;
            _this.option1 = variant['option1'];
            _this.option2 = variant['option2'];
            _this.option3 = variant['option3'];
          }
        });
      }

      // We update the selectors on initial load to disable the sold out variants
      this._updateSelectors(this.currentVariant); 

      this._attachListeners();
    }

    ProductVariants.prototype = Object.assign({}, ProductVariants.prototype, {

      _attachListeners: function(evt) {
        this.delegateElement.on('change', '.product-form__single-selector', this._onOptionChanged.bind(this));
        this.delegateElement.on('click', '[data-action="add-to-cart"]', this._addToCart.bind(this));
      },

      _onVariantChanged: function(previousVariant, newVariant) {
        this._updateProductPrices(newVariant, previousVariant);
        this._updateSelectors(newVariant, previousVariant);
        this._updateAddToCartButton(newVariant, previousVariant);

        // Finally, we send an event so 3rd party code can hook into this
        this.element.dispatchEvent(new CustomEvent('variant:changed', {
          bubbles: true,
          detail: {
            variant: newVariant,
            previousVariant: previousVariant
          }
        }));
      },

      _updateProductPrices: function(newVariant, previousVariant) {

        var productPrices = this.element.querySelector('[data-product-price-wrapper]');

        if (!productPrices) {
          return;
        }

        if (!newVariant) {
          // No variant exists so no price is available
          productPrices.style.display = 'none';
        } else {
          // Prices have not changed so return
          if (previousVariant && previousVariant['price'] === newVariant['price'] && previousVariant['compare_at_price'] === newVariant['compare_at_price']) {
            return;
          }

          if (newVariant['compare_at_price'] > newVariant['price']) {

            // Update product price
            productPrices.querySelector('[data-product-price]').innerHTML = theme.Currency.formatMoney(newVariant['price'], theme.settings.moneyFormat);

            // Update compare at price
            productPrices.querySelector('[data-product-compare-price]').innerHTML = theme.Currency.formatMoney(newVariant['compare_at_price'], theme.settings.moneyFormat);

            // Show compare at price and label
            productPrices.querySelector('[data-product-compare-price]').classList.remove('hide');
            productPrices.querySelector('[data-product-compare-price-label]').classList.remove('hide');

          } else {

            // Update product price
            productPrices.querySelector('[data-product-price]').innerHTML = theme.Currency.formatMoney(newVariant['price'], theme.settings.moneyFormat);

            // Hide compare at price and label
            productPrices.querySelector('[data-product-compare-price]').classList.add('hide');

            productPrices.querySelector('[data-product-compare-price-label]').classList.add('hide');
          }
        }
      },

      _updateSelectors: function(newVariant) {
        var _this2 = this;

        /**
         * We are using dynamic variant availability so users can clearly see
         * which variants are unavailable.
         * 
         * Shopify products can have up to three options e.g. Size, Colour, Length. 
         * We check each option in order. If there is more than one option, the 
         * option is considered "available" if there is at least one variant with 
         * this value available (independently of the selected variant)
         */

        var applyClassToSelector = function applyClassToSelector(selector, valueIndex, available) {
          var selectorType = selector.getAttribute('data-selector-type');

          switch (selectorType) {
            case 'color':
              selector.querySelector(".color-swatch:nth-child(".concat(valueIndex + 1, ")")).classList.toggle('color-swatch--disabled', !available);
              break;

            case 'variant':
              selector.querySelector(".variant-swatch:nth-child(".concat(valueIndex + 1, ")")).classList.toggle('variant-swatch--disabled', !available);
              break;

            case 'block':
              selector.querySelector(".block-swatch:nth-child(".concat(valueIndex + 1, ")")).classList.toggle('block-swatch--disabled', !available);
              break;
          }
        };

        if (this.variantSelectors && this.variantSelectors[0]) {
          // For the first option, the value is considered available if there is at least one variant available
          this.productOptionsWithValues[0]['values'].forEach(function (value, valueIndex) {
            applyClassToSelector(_this2.variantSelectors[0], valueIndex, _this2.productData['variants'].some(function (variant) {
              return variant['option1'] === value && variant['available'];
            })); 

            // We now move to the second option
            if (_this2.variantSelectors[1]) {
              _this2.productOptionsWithValues[1]['values'].forEach(function (value, valueIndex) {
                applyClassToSelector(_this2.variantSelectors[1], valueIndex, _this2.productData['variants'].some(function (variant) {
                  return variant['option2'] === value && variant['option1'] === _this2.option1 && variant['available'];
                })); 

                // And finally the third option
                if (_this2.variantSelectors[2]) {
                  _this2.productOptionsWithValues[2]['values'].forEach(function (value, valueIndex) {
                    applyClassToSelector(_this2.variantSelectors[2], valueIndex, _this2.productData['variants'].some(function (variant) {
                      return variant['option3'] === value && variant['option1'] === _this2.option1 && variant['option2'] === _this2.option2 && variant['available'];
                    }));
                  });
                }
              });
            }
          });
        }
      },

      _updateAddToCartButton: function(newVariant) {
        var addToCartButton = this.element.querySelector('[data-add-to-cart]');

        if (!addToCartButton) {
          return;
        }

        // Variant is unavailable
        if (!newVariant) {
          
          addToCartButton.setAttribute('disabled', 'disabled');
          addToCartButton.classList.add('btn--disabled');
          addToCartButton.classList.remove('btn--primary');
          addToCartButton.removeAttribute('data-action');
          addToCartButton.innerHTML = window.theme.strings.unavailable;
        } else {
          // Variant is available
          if (newVariant['available']) {
            addToCartButton.removeAttribute('disabled');
            addToCartButton.classList.remove('btn--disabled');
            addToCartButton.classList.add('btn--primary');
            addToCartButton.setAttribute('data-action', 'add-to-cart');
            addToCartButton.innerHTML = window.theme.strings.addToCart;
          } else {
            // Variant is sold out
            addToCartButton.setAttribute('disabled', 'disabled');
            addToCartButton.classList.add('btn--disabled');
            addToCartButton.classList.remove('btn--primary');
            addToCartButton.removeAttribute('data-action');
            addToCartButton.innerHTML = window.theme.strings.soldOut;
          }
        }

        if (this.options['showPaymentButton']) {
          var paymentButtonElement = this.element.querySelector('.shopify-payment-button');

          if (!paymentButtonElement) {
            return; // Some apps dynamically remove the dynamic checkout button from the code, so we add a guard here
          }

          if (!newVariant || !newVariant['available']) {
            paymentButtonElement.style.display = 'none';
          } else {
            paymentButtonElement.style.display = 'block';
          }
        }
      },

      _onOptionChanged: function(event, target) {
        this['option' + target.getAttribute('data-option-position')] = target.value; 

        // We update the selected value
        var selectedValueElement = target.closest('.product-form__option').querySelector('.product-form__selected-value');

        if (selectedValueElement) {
          selectedValueElement.innerHTML = target.value;
        } 

        // Finally, we get the new variant
        var previousVariant = this.currentVariant;
        this.currentVariant = this._getCurrentVariantFromOptions();

        this._onVariantChanged(previousVariant, this.currentVariant);

        if (this.currentVariant) {
          if (this.options['enableHistoryState'] && history.replaceState) {
            var newUrl = "".concat(window.location.protocol, "//").concat(window.location.host).concat(window.location.pathname, "?variant=").concat(this.currentVariant.id);
            window.history.replaceState({
              path: newUrl
            }, '', newUrl);
          } 

          // We need to modify the hidden select that contain the id attribute as well
          this.masterSelector.querySelector('[selected]').removeAttribute('selected');
          this.masterSelector.querySelector("[value=\"".concat(this.currentVariant['id'], "\"]")).setAttribute('selected', 'selected');
        }
      },

      _getCurrentVariantFromOptions: function() {
        var _this3 = this;

        var found = false;
        this.productData['variants'].forEach(function (variant) {
          if (variant['option1'] === _this3.option1 && variant['option2'] === _this3.option2 && variant['option3'] === _this3.option3) {
            found = variant;
          }
        });
        return found || null;
      },

      _addToCart: function(event, target) {
        console.log('_addToCart');
      }
    });

    return ProductVariants;
  })();










  /*==============================================================================
    #Product Gallery
  ==============================================================================*/

  theme.ProductGallery = (function() {

    function ProductGallery(element, options) {

      this.element = element;
      this.delegateElement = new Delegate(this.element);
      this.viewInSpaceElement = this.element.querySelector('[data-shopify-xr]');
      this.options = options;

      /**
       * The media property will hold all the media managed by the gallery so we can easily turn them off. They are indexed by the media ID.
       */
      this.media = {};
      this.previouslySelectedMedia = null;

      this._createCarousel();
      this._attachListeners();
    }

    ProductGallery.prototype = Object.assign({}, ProductGallery.prototype, {

      destroy: function() {
        if (this.flickityInstance) {
          this.flickityInstance.destroy();
        }

        for (var mediaId in this.media) {
          if (this.media.hasOwnProperty(mediaId)) {
            this.media[mediaId].destroy();
          }
        }
      },


      _attachListeners: function() {
        this.delegateElement.on('model:played', this._disableDrag.bind(this));
        this.delegateElement.on('video:played', this._disableDrag.bind(this));
        this.delegateElement.on('model:paused', this._enableDrag.bind(this));
        this.delegateElement.on('video:paused', this._enableDrag.bind(this));
      },


      variantHasChanged: function(newVariant) {

        var _this = this;

        /**
         * If we group variant images by ALT tag, we may need to rebuild our
         * slider to show only relevant images. We need to iterate through all
         * images, check for the attribute "data-group-name" and verify if some
         * images need to be hidden or not
         */
        var shouldReload = false;

        fastdom.mutate(function () {

          // Loop over each slide
          _this.productGalleryCellsElements.forEach(function (cell, imageIndex) {

            // If it has a data-group-name, we have grouped images by ALT tag
            if (cell.hasAttribute('data-group-name')) {
              
              // Get the group name which will be the relevant option e.g. Size, Colour, etc
              var groupName = cell.getAttribute('data-group-name');

              // Loop over the product options and match to the groupName
              _this.options['productOptions'].forEach(function (option, optionIndex) {
                if (option.toLowerCase() === groupName) {

                  // If the cell matches, we keep it in the slider. Otherwise, we hide it.
                  if (newVariant["option".concat(optionIndex + 1)].toLowerCase() === cell.getAttribute('data-group-value') || newVariant['featured_media'] && newVariant['featured_media']['id'] === parseInt(cell.getAttribute('data-media-id'))) {
                    cell.classList.remove('product-media__slide--is-hidden');

                    _this.productThumbnailsCellsElements[imageIndex].classList.remove('product-media__thumb--is-hidden');
                  } else {
                    cell.classList.add('product-media__slide--is-hidden');

                    _this.productThumbnailsCellsElements[imageIndex].classList.add('product-media__thumb--is-hidden');
                  }
                }
              });

              shouldReload = true;
            }
          });

          if (shouldReload) {
            /**
             * We rebuild the slider by using deactive - activate
             */
            _this.flickityInstance.deactivate();

            _this.flickityInstance.activate();
          }


          // Ensure to select the featured media after the slider has been built
          if (newVariant && newVariant['featured_media']) {
            _this.flickityInstance.selectCell("[data-media-id=\"".concat(newVariant['featured_media']['id'], "\"]"));
          }
        });
      },


      _createCarousel: function() {
        var _this2 = this;

        // Get slideshow element
        this.productGalleryElement = this.element.querySelector('[data-product-media-slider]');

        // Get slideshow slides
        this.productGalleryCellsElements = this.productGalleryElement ? this.productGalleryElement.querySelectorAll('.product-media__slide') : [];

        if (this.productGalleryElement) {

          // We need to create a list of media (for now we only handle video and model a bit differently)
          this.productGalleryCellsElements.forEach(function (item) {
            switch (item.getAttribute('data-media-type')) {
              case 'external_video':
              case 'video':
                _this2.media[item.getAttribute('data-media-id')] = new theme.ProductVideo(item, _this2.options['enableVideoLooping']);
                break;

              case 'model':
                _this2.media[item.getAttribute('data-media-id')] = new theme.ProductModel(item);
                break;
            }
          });

          // If we have more than one media item
          if (parseInt(this.productGalleryElement.getAttribute('data-media-count')) > 1) {

            // Due to the fact that some images may be hidden due to grouping media by alt tags, we have to do additional logic to compute the initial index
            var filteredCells = [].slice.call(this.productGalleryCellsElements).filter(function (item) {
              return !item.classList.contains('product-media__slide--is-hidden');
            });

            var initialIndex = 0;

            filteredCells.forEach(function (item, index) {
              if (item.getAttribute('data-media-id') === _this2.productGalleryElement.getAttribute('data-initial-media-id')) {
                initialIndex = index;
              }
            }); 

            /**
             * A hacky attempt to limit reflow. Flickity sets a zero height on initialisation, before replacing
             * with the correct height. This causes a page jump. We get around this by pre-setting the carousel
             * height to the first slide height
             */

            var firstSlide = filteredCells[initialIndex];
            firstSlide.classList.add('is-selected');
            this.productGalleryElement.style.height = "".concat(firstSlide.clientHeight, "px");

            /**
             * Initialise Flickity
             */
            this.flickityInstance = new Flickity(this.productGalleryElement, {
              accessibility: false,
              prevNextButtons: true,
              pageDots: false,
              adaptiveHeight: true,
              draggable: !theme.Responsive.matchesBreakpoint('supports-hover'),
              cellSelector: '.product-media__slide:not(.product-media__slide--is-hidden)',
              initialIndex: initialIndex,
              on: {
                ready: function ready() {
                  /**
                   * We remove the pre-set height that was used to minimise reflow, so the slider can react
                   * to changes of height
                   */
                  setTimeout(function () {
                    _this2.productGalleryElement.style.height = null;
                  }, 1000);
                }
              }
            });
          }
        }

        /**
         * Handle thumbnail clicks
         */
        this.productThumbnailsListElement = this.element.querySelector('.product-media__thumbs-list');
        this.delegateElement.on('click', '.product-media__thumb', this._onThumbnailClicked.bind(this));

        if (this.productThumbnailsListElement && this.flickityInstance) {
          this.productThumbnailsCellsElements = this.productThumbnailsListElement.querySelectorAll('.product-media__thumb');
          this.flickityInstance.on('select', this._onGallerySlideChanged.bind(this));

          this.flickityInstance.on('settle', this._onGallerySlideSettled.bind(this));

          // We call it once initially to force adjust the thumbnails
          this._onGallerySlideChanged(false);

          this._onGallerySlideSettled();
        }
      },


      _onGallerySlideChanged: function() {
        var _this5 = this;

        var previousNavElement = null;
        var newNavElement = null;

        /**
         * Set the active thumbnail
         */
        this.productThumbnailsCellsElements.forEach(function (item) {
          if (item.classList.contains('product-media__thumb--is-active')) {
            previousNavElement = item;
          }

          if (item.getAttribute('data-media-id') === _this5.flickityInstance.selectedElement.getAttribute('data-media-id')) {
            newNavElement = item;
          }
        });

        previousNavElement.classList.remove('product-media__thumb--is-active');
        newNavElement.classList.add('product-media__thumb--is-active');

      },


      /**
       * The difference with "change" is that this function is called after the item has transitioned
       */
      _onGallerySlideSettled: function() {
        this._handleMedia(this.flickityInstance.selectedElement);
      },

      _onThumbnailClicked: function(evt, target) {
        evt.preventDefault();

        if (this.flickityInstance) {
          this.flickityInstance.selectCell("[data-media-id=\"".concat(target.getAttribute('data-media-id'), "\"]"));
        }
      },


      _disableDrag: function() {
        this.flickityInstance.options.draggable = false;
        this.flickityInstance.updateDraggable();
      },


      _enableDrag: function() {
        this.flickityInstance.options.draggable = !theme.Responsive.matchesBreakpoint('supports-hover');
        this.flickityInstance.updateDraggable();
      },


      _handleMedia: function(item) {

        var isInitialLoading = this.previouslySelectedMedia === null; 

        // Turn off any previous media if any
        if (this.previouslySelectedMedia && this.previouslySelectedMedia !== item) {
          switch (this.previouslySelectedMedia.getAttribute('data-media-type')) {
            case 'video':
            case 'external_video':
            case 'model':
              this.media[this.previouslySelectedMedia.getAttribute('data-media-id')].hasBeenDeselected();
          } 

          // If the previous media was a model, we need to adjust the "view in space" button to go back to initial value

          if (this.previouslySelectedMedia.getAttribute('data-media-type') === 'model' && this.viewInSpaceElement) {
            this.viewInSpaceElement.setAttribute('data-shopify-model3d-id', this.viewInSpaceElement.getAttribute('data-shopify-model3d-default-id'));
          }
        }

        // Then, we have to handle the currently selected media
        switch (item.getAttribute('data-media-type')) {
          case 'video':
          case 'external_video':
          case 'model':
            this.media[item.getAttribute('data-media-id')].hasBeenSelected(isInitialLoading);
            break;

          case 'image':
            break;
        } 


        // If the media is a model, we need to adjust the "view in space" button
        if (item.getAttribute('data-media-type') === 'model' && this.viewInSpaceElement) {
          this.viewInSpaceElement.setAttribute('data-shopify-model3d-id', item.getAttribute('data-media-id'));
        } 

        // We set the previously selected media to the current new one, so when it changes it can be properly accounted for
        this.previouslySelectedMedia = item;
      }


    });

    return ProductGallery;
  })();










  /*==============================================================================
    #Product Video
  ==============================================================================*/

  theme.ProductVideo = (function() {

    function ProductVideo(element, enableVideoLooping) {
      this.element = element;
      this.delegateElement = new Delegate(this.element);
      this.enableVideoLooping = enableVideoLooping;
      this.player = null;

      switch (this.element.getAttribute('data-media-type')) {

        // If using a video, we load Shopify related media scripts
        case 'video':
          var stylesheet = document.createElement('link');
          stylesheet.rel = 'stylesheet';
          stylesheet.href = 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css';
          document.head.appendChild(stylesheet);
          window.Shopify.loadFeatures([{
            name: 'video-ui',
            version: '1.0',
            onLoad: this._setupHtml5Video.bind(this)
          }]);
          break;

        case 'external_video':
          this._setupExternalVideo();
          break;
      }
    }

    ProductVideo.prototype = Object.assign({}, ProductVideo.prototype, {
      
      destroy: function() {
        if (this.player) {
          this.player.destroy();
        }
      },


      hasBeenSelected: function(isInitialLoading) {
        /**
         * As per guidelines, we only need to autoplay when it's not a touch device,
         * and on desktop only if it's not the initial loading
         */
        if (theme.Responsive.matchesBreakpoint('supports-hover') && !isInitialLoading) {
          this.play();
        }
      },


      hasBeenDeselected: function() {
        this.pause();
      },


      play: function() {
        switch (this.element.getAttribute('data-media-type')) {
          case 'video':
            this.player.play();
            break;

          case 'external_video':
            this.player.playVideo(); 

            // If we're using YouTube, we have to focus the parent div (as it's not possible to directly focus an iframe)
            this.element.focus();
            break;
        }
      },

      pause: function() {
        switch (this.element.getAttribute('data-media-type')) {
          case 'video':
            this.player.pause();
            break;

          case 'external_video':
            this.player.pauseVideo();
            break;
        }
      },

      _setupHtml5Video: function() {
        var _this = this;

        this.player = new Shopify.Plyr(this.element.querySelector('video'), {
          controls: ['play', 'progress', 'mute', 'volume', 'play-large', 'fullscreen'],
          loop: {
            active: this.enableVideoLooping
          },
          hideControlsOnPause: true,
          clickToPlay: true,
          iconUrl: '//cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.svg',
          tooltips: {
            controls: false,
            seek: true
          }
        });
        this.player.on('play', function () {
          _this.element.dispatchEvent(new CustomEvent('video:played', {
            bubbles: true
          }));
        });
        this.player.on('pause', function () {
          _this.element.dispatchEvent(new CustomEvent('video:paused', {
            bubbles: true
          }));
        });
      },

      _setupExternalVideo: function() {
        if (this.element.getAttribute('data-media-host') === 'youtube') {
          this._loadYouTubeScript().then(this._setupYouTubePlayer.bind(this));
        }
      },

      _setupYouTubePlayer: function() {
        var _this2 = this;

        var playerLoadingInterval = setInterval(function () {
          if (window.YT !== undefined && window.YT.Player !== undefined) {
            _this2.player = new YT.Player(_this2.element.querySelector('iframe'), {
              videoId: _this2.element.getAttribute('data-video-id'),
              events: {
                onStateChange: function onStateChange(event) {
                  if (event.data === 0 && _this2.enableVideoLooping) {
                    event.target.seekTo(0);
                  }
                }
              }
            });
            clearInterval(playerLoadingInterval);
          }
        }, 50);
      },

      _loadYouTubeScript: function() {
        return new Promise(function (resolve, reject) {
          var script = document.createElement('script');
          document.body.appendChild(script);
          script.onload = resolve;
          script.onerror = reject;
          script.async = true;
          script.src = '//www.youtube.com/iframe_api';
        });
      }

    });

    return ProductVideo;
  })();  










  /*==============================================================================
    #Product Model
  ==============================================================================*/

  theme.ProductModel = (function() {

    function ProductModel(element) {
      this.element = element;
      this.delegateElement = new Delegate(this.element);
      this.delegateRoot = new Delegate(document.documentElement);

      this._attachListeners();

      // Load Shopify related scripts for 3d models
      var stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
      document.head.appendChild(stylesheet);
      window.Shopify.loadFeatures([{
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this._setupModelViewerUI.bind(this)
      }, {
        name: 'shopify-xr',
        version: '1.0'
      }]);
    }

    ProductModel.prototype = Object.assign({}, ProductModel.prototype, {

      destroy: function() {},

      _attachListeners: function() {
        var _this = this;

        this.element.querySelector('model-viewer').addEventListener('shopify_model_viewer_ui_toggle_play', function () {
          _this.element.dispatchEvent(new CustomEvent('model:played', {
            bubbles: true
          }));
        });
        this.element.querySelector('model-viewer').addEventListener('shopify_model_viewer_ui_toggle_pause', function () {
          _this.element.dispatchEvent(new CustomEvent('model:paused', {
            bubbles: true
          }));
        });
      },

      hasBeenSelected: function(isInitialLoading) {

        // As per guidelines, we only need to autoplay when it's not a touch device, and on desktop only if it's not the
        // initial loading
        if (theme.Responsive.matchesBreakpoint('supports-hover') && !isInitialLoading) {
          this.modelUi.play();
        }
      },

      hasBeenDeselected: function() {
        // In all cases, we just turn it off
        this.modelUi.pause();
      },

      _setupModelViewerUI: function() {
        console.log('run _setupModelViewerUI');
        this.modelElement = this.element.querySelector('model-viewer');
        this.modelUi = new window.Shopify.ModelViewerUI(this.modelElement);
      }

    });

    return ProductModel;
  })();










  /*==============================================================================
    #Product Zoom Modal
  ==============================================================================*/

  theme.ProductZoomModal = (function() {

    function ProductZoomModal() {
      this.gallery = document.querySelector('.product-media');
      this.modal = document.querySelector('#ProductZoomModal');
      this.delegateGallery = new Delegate(this.gallery);
      this.delegateModal = new Delegate(this.modal);
      this.delegateRoot = new Delegate(document.documentElement);

      this.modalIsOpen = false;

      this._attachListeners();
    }

    ProductZoomModal.prototype = Object.assign({}, ProductZoomModal.prototype, {

      _attachListeners: function() {
        // this._onBreakpointChangedListener = this._onBreakpointChanged.bind(this);
        // document.addEventListener('breakpoint:changed', this._onBreakpointChangedListener);

        // if (theme.Responsive.matchesBreakpoint('large')) {
        // }
        
        this.delegateGallery.on('click', '[data-zoom-open]', this._openModal.bind(this));
        this.delegateModal.on('click', '[data-zoom-close]', this._closeModal.bind(this));
        this._onKeyPressedListener = this._onKeyPressed.bind(this);
      },

      _detachListeners: function() {
        this.delegateGallery.off();
        this.delegateModal.off();
        this.delegateRoot.off('keyup', this._onKeyPressedListener);
        // document.removeEventListener('breakpoint:changed', this._onBreakpointChangedListener);
      },

      _openModal: function(event, target) {
        var _this = this;

        if (_this.modalIsOpen) {
          _this.closeModal();
        }

        this.modal.classList.add('zoom-modal--is-visible');
        this.modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('no-scroll');

        this.modalIsOpen = true;

        var zoomImage = target.getAttribute('data-zoom-image');
        var selectedImage = document.getElementById(zoomImage);
        var scrollPos = selectedImage.offsetTop;

        this.modal.scrollTo({
          behavior: 'smooth',
          top: scrollPos
        });

        this.delegateRoot.on('keyup', this._onKeyPressedListener);
      },

      _closeModal: function(event, target) {
        if (!this.modalIsOpen) {
          return;
        }

        this.modal.classList.remove('zoom-modal--is-visible');
        this.modal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('no-scroll');
        this.modalIsOpen = false;

        this.delegateRoot.off('keyup', this._onKeyPressedListener);
      },

      _onKeyPressed: function(event, target) {
        if (event.key === 'Escape') {
          this._closeModal();
        }
      },

      _onBreakpointChanged: function(event) {
        var currentBreakpoint = event ? event.detail.currentBreakpoint : theme.Responsive.getCurrentBreakpoint();
        var previousBreakpoint = event ? event.detail.previousBreakpoint : null;

        if (currentBreakpoint === previousBreakpoint) {
          return; // Nothing has changed, no specific setup to do
        } else {
          // If we are not on a large screen, we close modal and detach listeners for zoom
          this._closeModal();
          this._detachListeners();
        }

        // Reattach listeners which will check existing screen size, and include
        // relevant listeners
        this._attachListeners();
      }
    });

    return ProductZoomModal;
  })();










  /*==============================================================================
    #Quantity Picker
  ==============================================================================*/

  theme.QuantityPicker = (function() {

    function QuantityPicker(element) {
      if (!element) {
        return;
      }

      this.element = element;
      this.inputElement = this.element.querySelector('[name="quantity"]');
      this.delegateElement = new Delegate(this.element);

      this._attachListeners();
    }

    QuantityPicker.prototype = Object.assign({}, QuantityPicker.prototype, {

      _attachListeners: function() {
        this.delegateElement.on('click', '[data-action="decrease-picker-quantity"]', this._onDecrease.bind(this));
        this.delegateElement.on('click', '[data-action="increase-picker-quantity"]', this._onIncrease.bind(this));
        this.delegateElement.on('keyup', this._onInputValueChanged.bind(this));
        this.delegateElement.on('focusout', this._onInputFocusOut.bind(this));
      },

      _onDecrease: function() {
        this.inputElement.value = Math.max(1, parseInt(this.inputElement.value) - 1);
      },

      _onIncrease: function() {
        this.inputElement.value = parseInt(this.inputElement.value) + 1;
      },

      _onInputValueChanged: function(event) {
        var value = event.target.value;

        if (value !== '' && isNaN(value)) {
          event.target.value = Math.max(1, parseInt(value) || 1);
        }
      },

      _onInputFocusOut: function(event) {
        event.target.value = Math.max(1, parseInt(event.target.value) || 1);
      }
    });

    return QuantityPicker;
  })();










  /*==============================================================================
    #Cart
    - We're not using AJAX for cart behaviour. The following functionality helps
      with the quantity selectors within the cart.
  ==============================================================================*/

  theme.Cart = (function() {

    function Cart() {
      this.delegateRoot = new Delegate(document.documentElement);

      this._attachListeners();
    }

    Cart.prototype = Object.assign({}, Cart.prototype, {

      _attachListeners: function() {
        this.delegateRoot.on('click', '[data-action="decrease-quantity"]', this._updateQuantity.bind(this));

        this.delegateRoot.on('click', '[data-action="increase-quantity"]', this._updateQuantity.bind(this));

        this.delegateRoot.on('change', '.quantity-selector:not(.quantity-selector--product) .quantity-selector__value', this._updateQuantity.bind(this));

        this.delegateRoot.on('keyup', '.quantity-selector:not(.quantity-selector--product) .quantity-selector__value', this._updateQuantitySize.bind(this));

        this.delegateRoot.on('keydown', '.quantity-selector__value', this._blockEnterKey.bind(this));
      },

      _updateQuantity: function(event, target) {
        var _this = this;

        var parsedQuantity = 1;

        if (target.tagName === 'INPUT') {
          parsedQuantity = parseInt(target.value);
        } else {
          parsedQuantity = parseInt(target.getAttribute('data-quantity'));
        } 

        if (target.hasAttribute('data-href')) {
          window.location.href = target.getAttribute('data-href');
        } else {
          window.location.href = "".concat(window.routes.cartChangeUrl, "?line=").concat(target.getAttribute('data-line-index'), "&quantity=").concat(parsedQuantity);
        }

        return;
      },

      _updateQuantitySize: function(event, target) {
        target.setAttribute('size', Math.max(target.value.length, 2));
      },

      _blockEnterKey: function(event) {
        if (event.key === 'Enter') {
          return false;
        }
      }
    });

    return Cart;
  })();













  /*==============================================================================
    #Tabs
  ==============================================================================*/

  theme.Tabs = (function() {
    var delegateRoot = new Delegate(document.documentElement);

    var config = {
      activeTab: 'tab--is-active'
    };

    delegateRoot.on('click', '.tab', function(evt, target) {

      if (target.classList.contains(config.activeTab)) {
        return;
      }

      var targetTabId = target.getAttribute('id');
      var targetTab = document.querySelector('#' + targetTabId);
      var targetTabPanel = document.querySelector('[aria-labelledby="' + targetTabId + '-tab"]');

      document.querySelectorAll('.tab').forEach(function(item) {
        item.classList.remove(config.activeTab);
        item.setAttribute('aria-selected', 'false');
      });

      document.querySelectorAll('.tab__panel').forEach(function(item) {
        item.classList.remove(config.activeTab);
        item.setAttribute('aria-hidden', 'true');
      })

      targetTab.classList.add(config.activeTab);
      targetTab.setAttribute('aria-selected', 'true');
      targetTabPanel.classList.add(config.activeTab);
      targetTabPanel.setAttribute('aria-hidden', 'false');
    });
  })();










  /*==============================================================================
    #Scroll Snap Carousel
    - A CSS Scroll Snap carousel based on the following plugin 
      https://github.com/dimanech/scroll-snap-carousel
  ==============================================================================*/

  theme.ScrollSnapCarousel = (function() {

    function ScrollSnapCarousel(element) {
      if (!element) {
        return;
      }

      this.carousel = element;
      this.carouselScrollWrapper = this.carousel.querySelector('[data-elem-carousel-scroll-wrapper]');
      this.carouselTrack = this.carousel.querySelector('[data-elem-carousel-track]');

      this.prevButton = this.carousel.querySelector('[data-elem-prev-button]');
      this.nextButton = this.carousel.querySelector('[data-elem-next-button]');

      this.isDirectionHorizontal = true;
      this.currentPageIndex = 0;

      this.SCROLL_END_SENSITIVITY = 40; // Workaround IE rounding for clientWidth and scrollWidth

      this.stylesClass = {
        initialized: '_initialized',
        noScroll: '_no-scroll',
        prevVisible: '_prev-visible',
        nextVisible: '_next-visible',
        pagination: 'pagination',
        page: 'page',
        current: '_current'
      }

      this.init();
    }

    ScrollSnapCarousel.prototype = Object.assign({}, ScrollSnapCarousel.prototype, {

      init: function() {
        this.addEventListeners();
        this.onScroll();
        this.syncPrevNextButtons();
        this.carousel.classList.add(this.stylesClass.initialized);
      },

      addEventListeners: function() {
        this.onScroll = this.onScroll.bind(this);
        this.scrollBackward = this.scrollBackward.bind(this);
        this.scrollForward = this.scrollForward.bind(this);

        this.carouselScrollWrapper.addEventListener('scroll', this.onScroll, { passive: true });
        this.carouselScrollWrapper.addEventListener('touchstart', this.onScroll, { passive: true });
        this.prevButton.addEventListener('click', this.scrollBackward);
        this.nextButton.addEventListener('click', this.scrollForward);
      },

      removeEventListeners: function() {
        this.carouselScrollWrapper.removeEventListener('scroll', this.onScroll);
        this.carouselScrollWrapper.removeEventListener('touchstart', this.onScroll);
        this.prevButton.removeEventListener('click', this.scrollBackward);
        this.nextButton.removeEventListener('click', this.scrollForward);
      },

      // Prev next buttons and UI
      onScroll: function() {
        this.getScrollState();

        if (!this.isCallInNextFrameRequested) {
          window.requestAnimationFrame(this.scrollHandlers.bind(this));
          this.isCallInNextFrameRequested = true;
        }
      },

      getScrollState: function() {
        // Possible optimization: Resize Observer that watch carousel width and cache this.carousel.offsetWidth
        const totalScrollWidth = this.carouselScrollWrapper.scrollLeft + this.carousel.offsetWidth;

        this.isScrollStart = this.carouselScrollWrapper.scrollLeft <= 0;

        this.isScrollEnd = totalScrollWidth + this.SCROLL_END_SENSITIVITY >= this.carouselTrack.scrollWidth;
      },

      scrollHandlers: function() {
        this.syncPrevNextButtons();
        this.updateCurrentPageIndex();
        this.isCallInNextFrameRequested = false;
      },

      updateCurrentPageIndex: function() {
        const requestedPageIndex = Math.round(this.carouselScrollWrapper.scrollLeft / this.carousel.clientWidth);
        if (this.currentPageIndex === requestedPageIndex) {
          return;
        }

        this.onPageChanged(requestedPageIndex);

        this.currentPageIndex = requestedPageIndex;
        this.carousel.dispatchEvent(new CustomEvent('scrollCarousel:pageChanged', { bubbles: false, detail: this.currentPageIndex}));
      },

      syncPrevNextButtons: function() {
        if (this.isScrollStart && this.isScrollEnd) { // No scroll case
          this.carousel.classList.add(this.stylesClass.noScroll);
          this.hasScroll = false;
        } else {
          this.carousel.classList.remove(this.stylesClass.noScroll);
          this.hasScroll = true;
        }

        if (this.isScrollStart) {
          this.carousel.classList.remove(this.stylesClass.prevVisible);
          this.prevButton.setAttribute('disabled', '');
        } else {
          this.carousel.classList.add(this.stylesClass.prevVisible);
          this.prevButton.removeAttribute('disabled');
        }

        if (this.isScrollEnd) {
          this.carousel.classList.remove(this.stylesClass.nextVisible);
          this.nextButton.setAttribute('disabled', '');
        } else {
          this.carousel.classList.add(this.stylesClass.nextVisible);
          this.nextButton.removeAttribute('disabled');
        }
      },

      onPageChanged: function(requestedPageIndex) {
        this.setActivePagination(requestedPageIndex);
      },

      // Prev next functionality

      // relative scroll - page by page
      scrollBackward: function() {
        this.scrollByPage(false);
      },

      scrollForward: function() {
        this.scrollByPage(true);
      },

      scrollByPage: function(isNext) {
        const x = this.isDirectionHorizontal ? this.carouselTrack.clientWidth : 0;

        if (isNext) {
          this.carouselScrollWrapper.scrollBy(x, 0);
        } else {
          this.carouselScrollWrapper.scrollBy(-x, 0);
        }
      },

      // abs scroll - page to page
      getCurrentPageIndex: function() {
        const currentPosition = this.carouselScrollWrapper.scrollLeft;
        const pageWidth = this.carouselTrack.clientWidth;
        return Math.round(currentPosition / pageWidth);
      },

      scrollToNextPage: function() {
        this.scrollToPage(this.getCurrentPageIndex() + 1);
      },

      scrollToPrevPage: function() {
        this.scrollToPage(this.getCurrentPageIndex() - 1);
      },

      scrollToPage: function(pageIndex) {
        if (pageIndex < 0) {
          return;
        }

        this.scrollToPoint(Math.round(this.carousel.clientWidth * pageIndex));
      },

      scrollToPoint: function(top, left) {
        const element = this.carouselTrack;
        // Safari and Edge do not have smooth scrolling please use polyfill or just leave it as is
        // If you still using jQuery you could call $.animate()
        if (typeof element.scrollTo === 'function' && 'scrollBehavior' in document.documentElement.style) {
          element.scrollTo({
            left: left,
            behavior: 'smooth'
          });
        } else {
          element.scrollLeft = left;
        }
      },

      // Pagination

      initPagination: function() {
        if (!this.carousel.hasAttribute('data-pagination')) {
          return;
        }
        this.createPagination();
        this.updateCurrentPageIndex();
      },

      createPagination: function() {
        const hasPagination = !!this.pagination;
        // We need to use round, not ceil, since it called on scroll, in case of last it would generate false positive
        const numberOfPages = Math.round(this.carouselScrollWrapper.scrollWidth / this.carouselScrollWrapper.clientWidth);

        if (!hasPagination) {
          this.pagination = document.createElement('div');
          this.pagination.className = this.stylesClass.pagination;
        } else {
          this.carousel.removeChild(this.pagination);
        }

        for (let i = 0; i < numberOfPages; i++) {
          const page = document.createElement('div');
          page.className = this.stylesClass.page;
          page.setAttribute('data-page', i);
          this.pagination.appendChild(page);
        }

        if (!hasPagination) {
          this.carousel.appendChild(this.pagination);
        }
      },

      setActivePagination: function(requestedPageIndex) {
        if (!this.pagination) {
          return;
        }

        const pages = this.pagination.children;
        pages[this.currentPageIndex].classList.remove(this.stylesClass.current);
        pages[requestedPageIndex].classList.add(this.stylesClass.current);
      },

      destroyPagination: function() {
        if (!this.pagination) {
          return
        }
        this.carousel.removeChild(this.pagination);
        delete this.pagination;
      },

      // Destroy

      destroy: function() {
        this.removeEventListeners();
        this.destroyPagination();
      }

    });

    return ScrollSnapCarousel;
  })();










  /*==============================================================================
    #Product Section
  ==============================================================================*/

  theme.ProductSection = (function() {
    function ProductSection(container) {
      var container = this.container = container;
      this.element = this.container;

      var sectionId = this.sectionId = container.getAttribute('data-section-id');

      var options = this.options = JSON.parse(container.getAttribute('data-section-settings'));

      this.productGallery = new theme.ProductGallery(this.container, this.options);
      this.ProductVariants = new theme.ProductVariants(this.container, this.options);
      this.productZoomModal = new theme.ProductZoomModal(this.productGallery);

      var quantityPickerElement = this.element.querySelector('.quantity-selector--product');

      if (quantityPickerElement) {
        this.quantityPicker = new theme.QuantityPicker(quantityPickerElement);
      }

      this._attachListeners();
    }

    ProductSection.prototype = Object.assign({}, ProductSection.prototype, {
      _attachListeners: function() {
        this._onVariantChangedListener = this._onVariantChanged.bind(this);
        this.element.addEventListener('variant:changed', this._onVariantChangedListener);
      },

      _onVariantChanged: function(event) {
        this.productGallery.variantHasChanged(event.detail.variant);
      }
    });

    return ProductSection;
  })();










  /*==============================================================================
    #Product Recommendations
  ==============================================================================*/

  theme.ProductRecommendations = (function() {

    function ProductRecommendations(element) {

      this.element = element;
      this.delegateElement = new Delegate(this.element);

      this.options = JSON.parse(this.element.getAttribute('data-section-settings'));

      if (this.options['useRecommendations']) {
        this._loadRecommendations().then(this._createSlideshow.bind(this));
      }
    }

    ProductRecommendations.prototype = Object.assign({}, ProductRecommendations.prototype, {

      _loadRecommendations: function() {
        var _this = this;

        var url = "".concat(window.theme.routes.productRecommendationsUrl, "?section_id=product-recommendations&product_id=").concat(this.options['productId'], "&limit=").concat(this.options['recommendationsCount']);
        return fetch(url).then(function (response) {
          return response.text().then(function (content) {
            var container = document.createElement('div');
            container.innerHTML = content;
            _this.element.querySelector('.product-recommendations').innerHTML = container.querySelector('.product-recommendations').innerHTML;
          });
        });
      },

      _createSlideshow: function() {

        var _this2 = this;

        var carousel = _this2.element.querySelector('.carousel');
        new theme.ScrollSnapCarousel(carousel);
        theme.ProductItem();
      }

    });

    return ProductRecommendations;
  })();










  /*==============================================================================
    #Slideshow Section
  ==============================================================================*/

  theme.SlideshowSection = (function() {
    function SlideshowSection(container) {
      var container = this.container = container;

      var sectionId = this.sectionId = container.getAttribute('data-section-id');

      var options = this.options = JSON.parse(container.getAttribute('data-section-settings'));

      this.flickityInstance = new Flickity(container.querySelector('[data-slideshow]'), {
        pageDots: true,
        prevNextButtons: false,
        autoPlay: this.options['autoPlay'] ? this.options['cycleSpeed'] : false,
        cellAlign: 'left'
      });
    }

    SlideshowSection.prototype = Object.assign({}, SlideshowSection.prototype, {

      onUnload: function() {
        this.flickityInstance.destroy();
      },

      onBlockSelect: function(evt) {
        if (this.flickityInstance.isActive) {
          this.flickityInstance.selectCell(parseInt(evt.target.getAttribute('data-slide-index')), false, evt.detail.load);
          this.flickityInstance.pausePlayer();
        }
      },

      onBlockDeselect: function() {
        if (this.flickityInstance.isActive) {
          this.flickityInstance.unpausePlayer();
        }
      }
    });

    return SlideshowSection;
  })();










  /*==============================================================================
    #Hero Video Section
  ==============================================================================*/

  theme.HeroVideoSection = (function() {

    var selectors = {};

    var config = {};

    function HeroVideoSection(container) {

      // Store container as variable
      var container = this.container = container;

      // Store sectionId as variable
      var sectionId = this.sectionId = container.getAttribute('data-section-id');

      this.video = this.container.querySelector('[data-hero-video--' + sectionId + ']');

      this.init();
    }


    HeroVideoSection.prototype = Object.assign({}, HeroVideoSection.prototype, {

      init: function() {
        var self = this;

        if (this.video) {
          self.startMp4Playback(self.video).then(function() {
            // If playback runs
            self.setParentAsLoaded(self.video);
          }).catch(function(error) {
            // We need to add this class as it runs some CSS animation
            self.container.classList.add('video-loaded');
            // If no playback. Potentially low power mode in iOS.
            self.makeVideoInteractable(self.video);
          })
        }
      },

      startMp4Playback: function(mp4video) {
        return mp4video.play();
      },

      setParentAsLoaded: function(video) {
        this.container.classList.add('video-loaded');
      },

      makeVideoInteractable: function(video) {
        this.container.classList.add('video-interactable');
      },

      onSelect: function() {
        this.init();
      }
    });

    return HeroVideoSection;

  })();










  /*==============================================================================
    #Subscriptions
  ==============================================================================*/

  theme.Subscriptions = (function() {

    function Subscriptions(container) {
      var container = this.container = container;
      this.element = this.container;

      var sectionId = this.sectionId = container.getAttribute('data-section-id');

      var element = document.querySelector('[data-subscriptions]');
      var loader = document.querySelector('.loader');
      var subsContainer = document.querySelector('.subscriptions__container');

      this.delegateRoot = new Delegate(document.documentElement);
      this.delegateElement = new Delegate(element);
      this.loader = loader;
      this.subsContainer = subsContainer;

      this._attachListeners();
      this._setHeight();
    }

    Subscriptions.prototype = Object.assign({}, Subscriptions.prototype, {

      _attachListeners: function() {
        this.delegateElement.on('click', '[data-action="add-to-cart"]', this._addToCart.bind(this));

        this.delegateElement.on('click', '[data-action="next-screen"]', this._nextScreen.bind(this));

        this.delegateElement.on('click', '[data-action="select-subscription"]', this._selectSubscription.bind(this));

        this.delegateElement.on('click', '[data-action="navigate-screens"]', this._navigateScreens.bind(this));

        window.addEventListener('resize', this._resizeListener.bind(this));
      },


      _addToCart: function(event, target) {
        event.preventDefault();

        // Add loading styling
        this.loader.classList.add('is-loading');
        document.documentElement.classList.add('no-scroll');

        // Subscription products require additional properties passed in to the
        // form data
        var isSubscription = false;

        if (target.getAttribute('data-subscription-option') === 'ongoing') {
          isSubscription = true;
        }

        var formData = {
          "items": [{
            "id": target.getAttribute('data-variant-id'),
            "quantity": 1
          }]
        };

        /**
         * Subscription products require specific properties
         * Client is using the ReCharge Checkout from before November 2020
         * https://support.rechargepayments.com/hc/en-us/articles/360008683774-Adding-an-item-to-the-cart-with-AJAX
         */
        if (isSubscription) {
          formData = {
            "items": [{
              "id": target.getAttribute('data-variant-id'),
              "quantity": 1,
              "properties": {
                "shipping_interval_frequency": 3,
                "shipping_interval_unit_type": "Months"
              }
            }]
          }
        }


        fetch("".concat(window.theme.routes.cartAddUrl, ".js"), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        })
        .then(function (response) {
          if (response.ok) {
            window.location.href = window.theme.routes.cartUrl;
          } else {
            this.loader.classList.remove('is-loading');
            document.documentElement.classList.remove('no-scroll');
          }
        });

      },

      _setHeight: function(screen) {
        /**
         * Subscriptions screens are absolutely positioned meaning they are out
         * of the document flow. We need to set an explicit height.
         */

        if (screen) {
        } else {
          var screen = document.querySelector('.subscriptions__screen--is-active');
        }

        var outerHeight = screen.offsetHeight;
        this.subsContainer.style.height = outerHeight + 'px';
      },


      _nextScreen: function(event, target) {

        // Get current screen
        var currentScreen = target.closest('[data-screen]');
        var currentScreenIndex = parseInt(currentScreen.getAttribute('data-screen'));

        // Get new screen
        var newScreenIndex = currentScreenIndex + 1;
        var newScreen = document.querySelector('[data-screen="' + newScreenIndex +  '"]');

        // Set active screen index on subscriptions container
        this.subsContainer.setAttribute('data-active-screen', newScreenIndex);

        // Set height and active screen
        this._setHeight(newScreen);
        currentScreen.classList.remove('subscriptions__screen--is-active');
        newScreen.classList.add('subscriptions__screen--is-active');

        window.scrollTo({
          behavior: 'smooth',
          top: 0
        });
      },

      /**
       * This function runs for subscription buttons on screen 3, and 
       * subscription cards on screen 4.
       */
      _selectSubscription: function(event, target) {

        if (target.classList.contains('subscription-card--active')) {
          return;
        }

        var addToCart = document.querySelector('[data-add-to-cart]');

        // Add loading class to add to cart button
        addToCart.classList.add('button--loading');

        // remove active styling from any cards
        var activeCards = document.querySelectorAll('[data-subscription-card]');

        activeCards.forEach(function(element, index) {
          element.classList.remove('subscription-card--active');
        });

        // If making a selection on panel 3, we want the selected card to be
        // ordered first on panel 4. We remove any existing styling.
        if (target.hasAttribute('data-select-subscription-button')) {
          activeCards.forEach(function(element, index) {
            element.classList.remove('subscription-card--active-order');
          });
        }

        // Get selected product data for and push to add to cart button
        var selectedSubscription = target.getAttribute('data-subscription-option');
        var selectedVariantId = target.getAttribute('data-variant-id');

        addToCart.setAttribute('data-subscription-option', selectedSubscription);
        addToCart.setAttribute('data-variant-id', selectedVariantId);

        // Add active styling to selected subscription elements
        var cards = document.querySelectorAll('[data-subscription-card="' + selectedSubscription + '"]');

        cards.forEach(function(item, index) {
          item.classList.add('subscription-card--active');
        });

        // Remove loading class to add to cart button
        addToCart.classList.remove('button--loading');

        // If on panel 3, we proceed to screen 4, and set the active card
        if (target.hasAttribute('data-select-subscription-button')) {
          cards.forEach(function(item, index) {
            item.classList.add('subscription-card--active-order');
          });
          this._nextScreen(event, target);
        }
      },


      /**
       * Allows navigating using the icons or the back button
       */
      _navigateScreens: function(event, target) {

        // Get target screen
        var selectedNav = parseInt(target.getAttribute('data-nav'));
        var currentScreen = document.querySelector('.subscriptions__screen--is-active');
        var newScreen = document.querySelector('[data-screen="' + selectedNav +  '"]');

        // Set active screen index on subscriptions container
        this.subsContainer.setAttribute('data-active-screen', selectedNav);

        currentScreen.classList.remove('subscriptions__screen--is-active');

        this._setHeight(newScreen);
        newScreen.classList.add('subscriptions__screen--is-active');

        window.scrollTo({
          behavior: 'smooth',
          top: 0
        });
      },

      _resizeListener: function() {
        this._setHeight();
      }

    });

    return Subscriptions;
  })();










  /*==============================================================================
    #LoginSection
  ==============================================================================*/

  theme.LoginSection = (function() {

    function LoginSection(container) {

      this.container = container;
      this.domDelegate = new Delegate(container);

      this.customerLoginForm = container.querySelector('#customer_login');
      this.recoverPasswordForm = container.querySelector('#recover_customer_password');

      this.domDelegate.on('click', '[data-action="toggle-login-form"]', this.showRecoverPassword.bind(this));
    }


    LoginSection.prototype = Object.assign({}, LoginSection.prototype, {

      showRecoverPassword: function() {
        var isLoginActive = this.customerLoginForm.style.display === 'block';

        if (isLoginActive) {
          this.customerLoginForm.style.display = 'none';
          this.recoverPasswordForm.style.display = 'block';
        } else {
          this.customerLoginForm.style.display = 'block';
          this.recoverPasswordForm.style.display = 'none';
        }
      }
    });

    return LoginSection;

  })();









  /*==============================================================================
    #Account Section
  ==============================================================================*/

  theme.AccountSection = (function() {

    function AccountSection(container) {
      this.element = container;
      this.domDelegate = new Delegate(this.element);

      this.element.querySelectorAll('[action*="/account/addresses"]').forEach(function (addressForm) {
        new theme.CountrySelector(addressForm.querySelector('[name="address[country]"]'), addressForm.querySelector('[name="address[province]"]'));
      });

      this.pageSelector = new theme.ValuePicker('account-selector');
    }


    AccountSection.prototype = Object.assign({}, AccountSection.prototype, {
      _onUnload: function() {
        this.pageSelector.destroy();
      }
    });

    return AccountSection;

  })();










  /*==============================================================================
    #Country Selector
    - Used in account / addresses
  ==============================================================================*/

  theme.CountrySelector = (function() {

    function CountrySelector(countrySelect, provinceSelect) {

      // Store container as variable
      this.countrySelect = countrySelect;
      this.provinceSelect = provinceSelect;

      if (this.countrySelect && this.provinceSelect) {
        this._attachListeners();
        this._initSelectors();
      }
    }


    CountrySelector.prototype = Object.assign({}, CountrySelector.prototype, {

      destroy: function() {
        if (this.countrySelect) {
          this.countrySelect.removeEventListener('change', this._onCountryChangedListener);
        }
      },

      _initSelectors: function() {
        // Check first the default value of country
        var defaultCountry = this.countrySelect.getAttribute('data-default');

        if (defaultCountry) {
          for (var i = 0; i !== this.countrySelect.options.length; ++i) {
            if (this.countrySelect.options[i].text === defaultCountry) {
              this.countrySelect.selectedIndex = i;
              break;
            }
          }
        } else {
          this.countrySelect.selectedIndex = 0;
        }

        var event = new Event('change', {
          bubbles: true
        });
        this.countrySelect.dispatchEvent(event); 

        // Then the province
        var defaultProvince = this.provinceSelect.getAttribute('data-default');

        if (defaultProvince) {
          for (var _i = 0; _i !== this.provinceSelect.options.length; ++_i) {
            if (this.provinceSelect.options[_i].text === defaultProvince) {
              this.provinceSelect.selectedIndex = _i;
              break;
            }
          }
        }
      },

      _attachListeners: function() {
        this._onCountryChangedListener = this._onCountryChanged.bind(this);
        this.countrySelect.addEventListener('change', this._onCountryChangedListener);
      },

      _onCountryChanged: function() {
        var _this = this;

        var selectedOption = this.countrySelect.options[this.countrySelect.selectedIndex];

        if (!selectedOption) {
          return;
        }

        var provinces = JSON.parse(selectedOption.getAttribute('data-provinces') || '[]'); // First remove all options

        this.provinceSelect.innerHTML = '';

        if (provinces.length === 0) {
          this.provinceSelect.closest('.form__item').style.display = 'none';
          return;
        } // We need to build the provinces array

        provinces.forEach(function (data) {
          _this.provinceSelect.options.add(new Option(data[1], data[0]));
        });
        this.provinceSelect.closest('.form__item').style.display = 'block';
      }
    });

    return CountrySelector;

  })();











  /*==============================================================================
    #Gift Card Section
  ==============================================================================*/

  theme.GiftCardSection = (function() {

    function GiftCardSection(container) {
      this.element = container;
      this.delegateElement = new Delegate(this.element);

      this._createQrCode();

      this._attachListeners();
    }


    GiftCardSection.prototype = Object.assign({}, GiftCardSection.prototype, {

      onUnload: function() {
        this.delegateElement.off();
      },

      _createQrCode: function() {
        var qrCodeElements = document.querySelectorAll('.gift-card__qr');
        qrCodeElements.forEach(function (qrCodeElement) {
          new QRCode(qrCodeElement, {
            text: qrCodeElement.getAttribute('data-identifier'),
            width: 200,
            height: 200
          });
        });
      },

      _attachListeners: function() {
        this.delegateElement.on('click', '[data-action="print"]', this._print.bind(this));
        this.delegateElement.on('click', '[data-action="select-code"]', this._selectCode.bind(this));
      },

      _print: function() {
        window.print();
      },

      _selectCode: function(event, element) {
        element.select();
      }
    });

    return GiftCardSection;

  })();










  /*==============================================================================
    #Modal Manager
  ==============================================================================*/

  theme.ModalManager = (function() {
    function ModalManager() {

      this.domDelegate = new Delegate(document.body);

      this.activeModalsQueue = [];
      var activeModal = document.querySelector('.modal[aria-hidden="false"]');

      if (activeModal) {
        this.activeModalsQueue.push(activeModal);
      }

      this._attachListeners();
    }

    ModalManager.prototype = Object.assign({}, ModalManager.prototype, {

      _attachListeners: function() {

        this._onKeyPressedListener = this._onKeyPressed.bind(this);

        this.domDelegate.on('click', '[data-action="open-modal"]', this._openModal.bind(this));

        this.domDelegate.on('click', '[data-action="close-modal"]', this._closeModal.bind(this));

        this.domDelegate.on('click', '[data-action="remove-bundle-item"]', this._closeModal.bind(this));

        /**
         * We allow other code to close a modal by listening to the `modal:close`` event
         */
        document.addEventListener('modal:close', this._closeModal.bind(this));

        this._clickOutsideListener = this._checkClickOutside.bind(this);

        // If there is already an active modal when the page is loaded, we directly attach the listener on outside click
        if (this.activeModalsQueue.length > 0) {
          document.documentElement.classList.add('no-scroll');
          this.domDelegate.on('click', this._clickOutsideListener);
        }
      },


      _openModal: function(event, target) {

        var _this = this;

        var modal = document.querySelector("#".concat(target.getAttribute('aria-controls')));

        if (modal) {
          var onOpen = function onOpen(event) {
            if (event.propertyName === 'visibility') {
              modal.removeEventListener('transitionend', onOpen);
              theme.Accessibility.trapFocus(modal, 'modal' + modal.id);

              _this.activeModalsQueue.push(modal);
            }
          }
        };

        modal.addEventListener('transitionend', onOpen);

        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('no-scroll');
        this.domDelegate.on('click', this._clickOutsideListener);
        this.domDelegate.on('keyup', this._onKeyPressedListener);

        // Prevent the event to be propagated to the clickOutsideListener immediately
        return false;
      },


      _closeModal: function() {

        if (this.activeModalsQueue.length > 0) {
          var modalToClose = this.activeModalsQueue.pop();

          var onClose = function onClose(event) {
            if (event.propertyName === 'visibility') {
              modalToClose.removeEventListener('transitionend', onClose);

              theme.Accessibility.removeTrapFocus(modalToClose, 'modal' + modalToClose.id);

              // We emit an event saying it has closed, which can give the option to do cleanup
              modalToClose.dispatchEvent(new CustomEvent('modal:closed'));
            }
          }
        };

        modalToClose.addEventListener('transitionend', onClose);

        modalToClose.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('no-scroll');
        this.domDelegate.off('click', this._clickOutsideListener);
        this.domDelegate.off('keyup', this._onKeyPressedListener);
      },


      _checkClickOutside: function(event) {
        if (this.activeModalsQueue.length === 0) {
          return;
        }

        var modalToClose = this.activeModalsQueue[this.activeModalsQueue.length - 1];

        if (modalToClose && (!modalToClose.contains(event.target) || modalToClose === event.target)) {
          this._closeModal();
        }
      },


      _onKeyPressed: function(event) {
        if (event.key === 'Escape') {
          this._closeModal();
        }
      }
    });

    return ModalManager;
  })();










  /*==============================================================================
    #Value Picker
  ==============================================================================*/

  /**
   * The given ID must be the value picker itself (the element that contains the class "value-picker", not the button that toggles it
   */

  theme.ValuePicker = (function() {
    function ValuePicker(id) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      this.id = id;
      this.delegateRoot = new Delegate(document.documentElement);
      this.isOpen = false;

      this.togglerElement = document.querySelector("[data-action=\"open-value-picker\"][aria-controls=\"".concat(this.id, "\"]"));

      this.onSelect = options['onValueSelect'] || function () {};

      this._attachListeners();
    }

    ValuePicker.prototype = Object.assign({}, ValuePicker.prototype, {
      destroy: function() {
        this.delegateRoot.off();
      },

      _attachListeners: function() {
        this.delegateRoot.on('click', "[data-action=\"open-value-picker\"][aria-controls=\"".concat(this.id, "\"]"), this._toggle.bind(this));
        this.delegateRoot.on('click', "[data-action=\"close-value-picker\"][aria-controls=\"".concat(this.id, "\"]"), this._toggle.bind(this));
        this.delegateRoot.on('click', "#".concat(this.id, " [data-action=\"select-value\"]"), this._selectValue.bind(this));
        this.delegateRoot.on('click', this._detectOutsideClick.bind(this), true);
        this.delegateRoot.on('focusout', "#".concat(this.id), this._onFocusOut.bind(this));
      },

      _toggle: function(event) {
        if (this.isOpen) {
          this._close(event);
        } else {
          this._open(event);
        }
      },

      _open: function() {
        document.querySelector("[data-action=\"open-value-picker\"][aria-controls=\"".concat(this.id, "\"]")).setAttribute('aria-expanded', 'true');
        document.getElementById(this.id).setAttribute('aria-hidden', 'false'); 

        // This is quite ugly, but in order to avoid an issue with the header that has a higher z-index, we have to temporarily reduce
        // it while we have the element open

        if (theme.Responsive.matchesBreakpoint('small')) {
          var headerElement = document.querySelector('.shopify-section--header');
          headerElement.style.zIndex = '3';
        }

        this.isOpen = true;
        document.body.classList.add('no-mobile-scroll');
      },

      _close: function() {
        document.querySelector("[data-action=\"open-value-picker\"][aria-controls=\"".concat(this.id, "\"]")).setAttribute('aria-expanded', 'false');
        document.getElementById(this.id).setAttribute('aria-hidden', 'true'); 

        // This is quite ugly, but in order to avoid an issue with the header that has a higher z-index, we have to temporarily reduce
        // it while we have the element open

        var headerElement = document.querySelector('.shopify-section--header');
        headerElement.style.zIndex = '';
        this.isOpen = false;
        document.body.classList.remove('no-mobile-scroll');
      },

      _selectValue: function(event, target) {
        this.onSelect(target.getAttribute('data-value'));

        this._close();
      },

      _onFocusOut: function(event) {
        var container = document.getElementById(this.id); // If the element that get the new focus is not contains within the value picker, we close it

        if (!container.contains(event.relatedTarget)) {
          this._close();
        }
      },

      _detectOutsideClick: function(event) {
        // If already closed, nothing to do
        if (!this.isOpen || this.togglerElement === event.target || this.togglerElement.contains(event.target)) {
          return;
        }

        if (!event.target.closest('.value-picker__inner') && this.isOpen) {
          this._close(event);
        }
      }
    });

    return ValuePicker;
  })();




















  /*==============================================================================
    #Tabbing - Handle tabbing for help with accessibility styling
  ==============================================================================*/

  (function () {
    function handleFirstTab(event) {
      if (event.key === 'Tab') {
        document.body.classList.add('is-tabbing');
        window.removeEventListener('keydown', handleFirstTab);
      }
    }

    window.addEventListener('keydown', handleFirstTab);
  })();










  /*==============================================================================
    #Page Transitions
  ==============================================================================*/

  window.onpageshow = function(evt) {
    // Removes unload class when returning to page via history
    if (evt.persisted) {
      document.body.classList.remove('unloading');
      document.querySelectorAll('.cart__checkout').forEach(el => {
        el.classList.remove('btn--loading');
      });
    }
  };

  theme.PageTransitions = function() {
    if (document.body.dataset.pageTransitions === 'true') {

      var delegateBody = new Delegate(document.body);

      /**
       * Hack test to fix Safari page cache issue.
       * `window.onpageshow` doesn't always run when navigating back to the page
       * so the unloading class remains, leaving a white page. Setting a timeout
       * to remove that class when leaving the page finishes running when they
       * come back
       */
      if (!!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/)) {
        document.querySelectorAll('a').forEach(a => {
          window.setTimeout(function() {
            document.body.classList.remove('unloading');
          }, 1200);
        });
      }

      // Add disable transition class to various link types
      delegateBody.on('click', 'a[href]:not([href^="#"]):not([href^="javascript:"]):not([href^="mailto:"]):not([href^="tel:"]):not([target="_blank"]):not([data-prevent-link])', onPageUnload)

      function onPageUnload(evt, target) {
        if (evt.metaKey) {
          return;
        }

        evt.preventDefault();
        evt.stopPropagation();

        document.body.classList.add('unloading');

        var src = target.getAttribute('href');
        
        window.setTimeout(function() {
          location.href = src;
        }, 50);

      }
    }
  };










  /*==============================================================================
    #Product Item
    - Initialises quantity selector on hover for all product items
  ==============================================================================*/

  theme.ProductItem = function() {
    // Initialise quantity selector on product items
    var itemForm = document.querySelectorAll('.product-item__form');
    
    itemForm.forEach(function(element, index){
      var quantityPickerElement = element.querySelector('.quantity-selector--product-item');

      if (quantityPickerElement) {
        new theme.QuantityPicker(quantityPickerElement);
      }
    });
  }




















  /*==============================================================================
    #Dom Ready
  ==============================================================================*/

  function DOMready(callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  }

  DOMready(function(){
    theme.PageTransitions();
    theme.ProductItem();
    new theme.ModalManager();
    new theme.Cart();

    var carousel = document.querySelector('.carousel');
    new theme.ScrollSnapCarousel(carousel);

    // Sal animation library
    sal();

    // Jarallax parallax library
    jarallax(document.querySelectorAll('.jarallax'), {
        speed: 0.5
    });

    theme.sections = new theme.Sections();
    theme.sections.register('header-section', theme.HeaderSection);
    theme.sections.register('slideshow-section', theme.SlideshowSection);
    theme.sections.register('hero-video-section', theme.HeroVideoSection);
    theme.sections.register('product-template-section', theme.ProductSection);
    theme.sections.register('product-recommendations-section', theme.ProductRecommendations);
    theme.sections.register('subscriptions-section', theme.Subscriptions);
    theme.sections.register('login-template-section', theme.LoginSection);
    theme.sections.register('account-template-section', theme.AccountSection);
    theme.sections.register('giftcard-template-section', theme.GiftCardSection);
  });
})();